import { useEffect, useState } from 'react';

import { Circle, PeopleAlt, Person, Visibility } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { If } from 'src/components/If';

import { ShareWalletModal } from '../../Wallet/components/ShareWalletModal';
import { WalletFundHealthStatus } from '../WalletFundHealthStatus';

export type WalletCardProps = {
  id: number;
  name: string;
  ownerName?: string;
  fundsCount: number;
  quotasCount: number;
  criticality?: {
    alertCount: number;
    criticalCount: number;
    healthyCount: number;
    walletSummaryCount: number;
  };
  isSharedWallet?: boolean;
  useWalletHealthIndicators?: boolean;
  walletButton?: {
    label: string;
    redirectTo: string;
  };
};

enum CriticalityWalletLevel {
  CRITICAL = '#FF4747',
  ALERT = '#FFCF66',
  NO_ALERT = '#82CE57',
  NONE = '#C8C8C8',
}

const CRITICALITY_ALERT_POSITIONS = {
  CRITICAL: 0,
  ALERT: 1,
  NO_ALERT: 2,
};

const walletHealthIndicators = [
  {
    title: 'Crítico',
    color: CriticalityWalletLevel.CRITICAL,
    amount: 0,
  },
  {
    title: 'Atenção',
    color: CriticalityWalletLevel.ALERT,
    amount: 0,
  },
  {
    title: 'Sem Alerta',
    color: CriticalityWalletLevel.NO_ALERT,
    amount: 0,
  },
];

export const WalletCard = ({
  id: walletId,
  name,
  ownerName,
  fundsCount,
  quotasCount,
  criticality,
  isSharedWallet,
  walletButton,
  useWalletHealthIndicators = true,
}: WalletCardProps) => {
  const lineStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 1,
  };

  const [walletCriticality, setWalletCriticality] = useState(criticality);
  const [criticalityWalletLevel, setCriticalityWalletLevel] = useState(
    CriticalityWalletLevel.NONE,
  );
  const [openShareModal, setOpenShareModal] = useState(false);

  walletHealthIndicators[CRITICALITY_ALERT_POSITIONS.CRITICAL].amount =
    walletCriticality?.criticalCount || 0;
  walletHealthIndicators[CRITICALITY_ALERT_POSITIONS.ALERT].amount =
    walletCriticality?.alertCount || 0;
  walletHealthIndicators[CRITICALITY_ALERT_POSITIONS.NO_ALERT].amount =
    walletCriticality?.healthyCount || 0;

  useEffect(() => {
    const {
      criticalCount = 0,
      alertCount = 0,
      healthyCount = 0,
    } = walletCriticality || {};

    const level =
      criticalCount > 0
        ? CriticalityWalletLevel.CRITICAL
        : alertCount > 0
          ? CriticalityWalletLevel.ALERT
          : healthyCount > 0
            ? CriticalityWalletLevel.NO_ALERT
            : CriticalityWalletLevel.NONE;

    setCriticalityWalletLevel(level);
  }, [walletCriticality]);

  return (
    <>
      <Card
        sx={{
          px: 1,
          position: 'relative',
          minHeight: useWalletHealthIndicators ? 280 : 150,
        }}>
        <CardHeader
          avatar={
            isSharedWallet ? (
              <IconButton
                title="Compartilhada"
                onClick={() => setOpenShareModal(true)}
                sx={{ p: 0 }}>
                <PeopleAlt sx={{ color: 'text.disabled' }} />
              </IconButton>
            ) : undefined
          }
          title={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                minWidth: 0,
              }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography
                  variant="h6"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mr: 1,
                  }}>
                  {name}
                </Typography>
                <If condition={ownerName}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <Person sx={{ color: 'text.disabled' }} />
                    <Typography
                      sx={{
                        flex: 1,
                        textAlign: 'end',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mr: 1,
                        mt: 1.25,
                        fontWeight: 'bold',
                        fontSize: 12,
                        color: 'text.disabled',
                      }}>
                      {ownerName}
                    </Typography>
                  </Box>
                </If>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  flexShrink: 0,
                  ml: 1,
                  display: 'flex',
                  alignItems: 'center',
                }}>
                <Circle sx={{ color: criticalityWalletLevel, fontSize: 14 }} />
              </Typography>
            </Box>
          }
          sx={{
            my: 0,
            '& .MuiCardHeader-content': {
              width: '100%',
              minWidth: 0,
            },
          }}
        />
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}>
          <Box sx={lineStyles}>
            <Typography py={1} variant="body2">
              Subclasse de Cotas/Fundos
            </Typography>
            <Typography py={1} variant="body2" fontWeight="bold">
              {quotasCount}/{fundsCount}
            </Typography>
          </Box>
          <If condition={useWalletHealthIndicators}>
            <Box pb={2}>
              {walletHealthIndicators.map((indicator) => (
                <WalletFundHealthStatus
                  key={indicator.title}
                  title={indicator.title}
                  color={indicator.color}
                  amount={indicator.amount}
                />
              ))}
            </Box>
          </If>
          <Divider sx={{ borderColor: '#a4a4a4', mb: -3 }} />
        </CardContent>
        <CardActions sx={lineStyles}>
          <Button
            variant="text"
            component={Link}
            to={walletButton?.redirectTo ?? `/monitor/wallet/${walletId}`}
            fullWidth
            sx={{ color: '#646464', fontSize: 10, textTransform: 'unset' }}
            startIcon={<Visibility />}>
            {walletButton?.label ?? 'Clique para gerenciar'}
          </Button>
        </CardActions>
      </Card>
      <ShareWalletModal
        walletId={walletId ?? 0}
        open={openShareModal}
        onClose={() => setOpenShareModal(false)}
        walletName={name}
      />
    </>
  );
};
